import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Loader2 } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { useInfiniteAllergies } from '../../hooks/useInfiniteAllergies';
import type { Medication } from '../../types/api';

interface AllergyInputProps {
  onAdd: (medication: Medication) => void;
  placeholder?: string;
  disabled?: boolean;
}

const AllergyInput: React.FC<AllergyInputProps> = ({ 
  onAdd, 
  placeholder = "Search medications...",
  disabled = false 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Use infinite scroll hook for medication loading
  const {
    medications,
    loading,
    loadingMore,
    hasMore,
    error,
    loadInitial,
    loadMore,
    search,
    reset
  } = useInfiniteAllergies(20);

  // Handle input focus - load initial medications
  const handleFocus = async () => {
    if (!hasInitiallyLoaded && medications.length === 0) {
      await loadInitial();
      setHasInitiallyLoaded(true);
      setShowDropdown(true);
    } else if (medications.length > 0) {
      setShowDropdown(true);
    }
  };

  // Search medications when search term changes
  useEffect(() => {
    const performSearch = async () => {
      if (searchTerm.trim().length === 0) {
        // If search is empty, revert to initial medications
        if (hasInitiallyLoaded) {
          await loadInitial();
          setShowDropdown(true);
        }
        return;
      }

      if (searchTerm.trim().length >= 2) {
        await search(searchTerm.trim());
        setShowDropdown(true);
        setSelectedIndex(-1);
      } else {
        // For 1 character, show initial medications but don't hide dropdown
        if (hasInitiallyLoaded) {
          await loadInitial();
          setShowDropdown(true);
        }
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, search, loadInitial, hasInitiallyLoaded]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || medications.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < medications.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && medications[selectedIndex]) {
          handleAddMedication(medications[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleAddMedication = (medication: Medication) => {
    onAdd(medication);
    setSearchTerm('');
    setShowDropdown(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Handle scroll for infinite loading
  const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    
    // Load more when scrolled to bottom (with 50px threshold)
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !loadingMore) {
      await loadMore();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-12"
        />
        {loading && (
          <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-vitl-green rounded-full"></div>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
          onScroll={handleScroll}
        >
          {medications.length > 0 ? (
            <>
              {medications.map((medication, index) => (
                <button
                  key={medication.id}
                  type="button"
                  onClick={() => handleAddMedication(medication)}
                  className={`w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 ${
                    index === selectedIndex ? 'bg-vitl-green/10' : ''
                  }`}
                >
                  <div className="font-medium text-gray-900">{medication.name}</div>
                  <div className="text-sm text-gray-500">{medication.category}</div>
                </button>
              ))}
              
              {/* Loading more indicator */}
              {loadingMore && (
                <div className="px-4 py-3 text-center border-t border-gray-100">
                  <div className="flex items-center justify-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin text-vitl-green" />
                    <span className="text-sm text-gray-500">Loading more...</span>
                  </div>
                </div>
              )}
              
              {/* No more results indicator */}
              {!hasMore && medications.length > 0 && (
                <div className="px-4 py-2 text-center border-t border-gray-100">
                  <span className="text-xs text-gray-400">No more results</span>
                </div>
              )}
            </>
          ) : loading ? (
            <div className="px-4 py-8 text-center">
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="w-4 h-4 animate-spin text-vitl-green" />
                <span className="text-sm text-gray-500">Loading medications...</span>
              </div>
            </div>
          ) : error ? (
            <div className="px-4 py-4 text-center">
              <span className="text-sm text-red-500">{error}</span>
            </div>
          ) : (
            <div className="px-4 py-4 text-center">
              <span className="text-sm text-gray-500">No medications found</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AllergyInput;